import { inject } from '@angular/core';
import { MatSnackBar } from '@angular/material/snack-bar';
import { AuthService, ResetPasswordCommand } from '@anihis/core/data-access';
import { patchState, signalStore, withMethods, withState } from '@ngrx/signals';
import { translate } from '@jsverse/transloco';
import { catchError, finalize, map, Observable, of, tap } from 'rxjs';
import { LoadingStatus } from '../../shared/enums/loading';
import { extractServerErrorMessage } from '../../shared/functions/extract-server-error';

type ResetPasswordState = {
  status: LoadingStatus;
  error: string | null;
  success: boolean;
};

const initialState: ResetPasswordState = {
  status: LoadingStatus.Loaded,
  error: null,
  success: false,
};

export const ResetPasswordStore = signalStore(
  withState(initialState),
  withMethods(
    (
      store,
      authService = inject(AuthService),
      snackBar = inject(MatSnackBar),
    ) => ({
      resetPassword(
        newPassword: string,
        userId: string,
        token: string,
      ): Observable<boolean> {
        if (!userId || !token) {
          snackBar.open(
            translate('Invalid reset password link. Please request a new one.'),
            translate('Close'),
            {
              duration: 7000,
              horizontalPosition: 'center',
              verticalPosition: 'top',
              panelClass: ['error-snackbar'],
            },
          );
          return of(false);
        }

        patchState(store, { status: LoadingStatus.Loading, error: null });

        const command: ResetPasswordCommand = {
          newPassword,
          userId,
          token,
        };

        return authService.authResetPasswordPost(command).pipe(
          tap(() => {
            patchState(store, { success: true, error: null });
          }),
          map(() => true as const),
          catchError((err) => {
            const errorMsg = extractServerErrorMessage(
              err,
              'Failed to reset password. The link may have expired.',
            );

            patchState(store, { error: errorMsg, success: false });

            return of(false as const);
          }),
          finalize(() => patchState(store, { status: LoadingStatus.Loaded })),
        );
      },

      resetError() {
        patchState(store, { error: null });
      },
    }),
  ),
);
